
import Vue from 'vue';
import _isEmpty from 'lodash/isEmpty';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	SNACKBAR_STATUS,
} from '@/constants/constants';
import { db } from '@/firebase';
import LoginSettingsDialog from '@/components/Toolbar/UserAccount/LoginSettingsDialog.vue';
import { User } from '@run-diffusion/shared';
import SettingsWindowItem from '@/components/Toolbar/UserAccount/SettingsWindowItem.vue';
import SessionsHistoryDialog from '@/components/Toolbar/UserAccount/SessionsHistoryDialog.vue';
import PaymentsHistoryDialog from '@/components/Toolbar/UserAccount/PaymentsHistoryDialog.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import { ROUTER } from '@/router/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { TeamUserMixin } from '@/mixins';
import { getDomainClaimRestrictionMessage } from '@/utils/domainClaimRestriction';

export default Vue.extend({
	name: 'UserAccount',
	mixins: [
		TeamUserMixin,
	],
	props: {
		menuResetTrigger: { type: Number, default: 0 },
	},
	data () {
		return {
			RUNNITS_OWNER_SELECTION,
			windowView: 0,
			ACCOUNT_MENU_WINDOW_ITEM: 0,
			EDIT_ACCOUNT_WINDOW_ITEM: 1,
			SESSION_SETTINGS_WINDOW_ITEM: 2,

			drawerTitle: 'Edit Account',

			deletingUser: false,
			accountDeletionRequestedAt: null,

			sessionsHistoryDialogOpen: false,
			paymentsHistoryDialogOpen: false,
			showDomainRestrictionPlanNudge: false,
			domainRestrictionPlanNudgeTimer: null as ReturnType<typeof setTimeout> | null,
		};
	},
	destroyed () {
		if (this.domainRestrictionPlanNudgeTimer) {
			clearTimeout(this.domainRestrictionPlanNudgeTimer);
			this.domainRestrictionPlanNudgeTimer = null;
		}
	},
	watch: {
		user: {
			immediate: true,
			handler (newVal: User) {
				if (newVal) {
					this.accountDeletionRequestedAt = localStorage.getItem(`ACCOUNT_DELETION_REQUESTED_AT_${newVal.id}`) || null;
				}
			},
		},
		menuResetTrigger: {
			immediate: true,
			handler (newVal: number, oldVal: number) {
				if (newVal !== oldVal) {
					this.windowView = this.ACCOUNT_MENU_WINDOW_ITEM;
				}
			},
		},
		showStripeUserMembershipPortalBtn: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (
					newVal !== oldVal &&
					newVal &&
					this.appQueryParamsData &&
					this.appQueryParamsData.openManageMembership
				) {
					// Auto open customer portal
					this.goToStripeSubscriptionPortal(null);
					// Reset openManageMembership to false
					this.updateAppQueryParamsData({ openManageMembership: false });
				}
				this.syncDomainRestrictionPlanNudge();
			},
		},
		deviceReportLocal: {
			immediate: true,
			handler () {
				this.syncDomainRestrictionPlanNudge();
			},
		},
		runnitState: {
			immediate: true,
			handler () {
				this.syncDomainRestrictionPlanNudge();
			},
			deep: true,
		},
	},
	computed: {
		...mapGetters([
			'displayLabels',
			'showStripeUserMembershipPortalBtn',
			'activeDomainClaimRestriction',
		]),
		...mapState([
			'user',
			'team',
			'toolbar',
			'appQueryParamsData',
			'runnitState',
			'deviceReportLocal',
		]),
		isScimProvisionedUser (): boolean {
			return Object.keys(this.user?.scim || {}).length > 0;
		},
		domainRestrictionMessage (): string {
			return getDomainClaimRestrictionMessage(this.activeDomainClaimRestriction, this.displayLabels.team);
		},
		shouldShowDomainRestrictionPlanNudge (): boolean {
			return !!(
				this.activeDomainClaimRestriction &&
				this.runnitState?.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER &&
				this.showStripeUserMembershipPortalBtn
			);
		},
	},
	methods: {
		_isEmpty,
		...mapActions([
			'updateToolbar',
			'updateSnackbar',
			'updateAppQueryParamsData',
			'goToStripeSubscriptionPortal',
			'setUpsellDialog',
		]),
		emitOnDrawerClose () {
			this.$emit('on-drawer-close');
		},
		onOpenChangeLoginSettingsDialog () {
			if (this.isScimProvisionedUser) return;
			this.updateToolbar({
				changeLoginSettingsDialogConfig: {
					open: true,
				},
			});
		},
		async handleDeleteUserConfirm (closeAndResetCallback: Function, doLogout: boolean) {
			// TODO: Need to just do all the deletion logic in a FB function and not do this request stuff (remove the "accountDeletionRequestedAt" property from state)
			this.deletingUser = true;
			const onError: Function = (e) => {
				console.error('Error requesting for user account deletion: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error deleting your account, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			};

			try {
				const deleteUserRequestRef: any = db.doc(`deleteUserRequests/${this.user.id}`);
				await deleteUserRequestRef.set({
					userId: this.user.id,
					createdAt: new Date(),
				});
				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: `Success! Your request has been recorded.`,
					show: true,
				});
				localStorage.setItem(`ACCOUNT_DELETION_REQUESTED_AT_${this.user.id}`, new Date().toISOString());
				this.accountDeletionRequestedAt = new Date().toISOString();

				if (doLogout) {
					this.$emit('logout', false);
				}
			} catch (e) {
				onError(e);
			} finally {
				this.deletingUser = false;
				if (closeAndResetCallback) {
					closeAndResetCallback();
				}
			}
		},
		openLogoutDialog () {
			this.updateToolbar({
				logoutDialogOpen: true,
			});
		},
		goToTeamRoute () {
			if (this.toolbar.session) {
				const routeData = this.$router.resolve({ name: ROUTER.COMPANY_DASHBOARD });
				window.open(routeData.href, '_blank');
			} else {
				this.routerPush(this.$route, this.$router, { name: ROUTER.COMPANY_DASHBOARD });
				this.emitOnDrawerClose();
			}
		},
		goToAdminRoute (routeName: string) {
			this.routerPush(this.$route, this.$router, { name: routeName });
			this.emitOnDrawerClose();
		},
		goToRetool () {
			window.open('https://rundiffusion.retool.com/apps/a867e538-cdd3-11ed-ae7f-cb1437cbeea9/Prod%20-%20RunDiffusion%20Admin%20Tools', '_blank');
		},
		goToRdDashboard () {
			window.open('https://rd-dashboard-33b4e1cfee6d.herokuapp.com/login', '_blank');
		},
		syncDomainRestrictionPlanNudge () {
			if (this.shouldShowDomainRestrictionPlanNudge) {
				this.showDomainRestrictionPlanNudge = true;
			} else {
				this.showDomainRestrictionPlanNudge = false;
				if (this.domainRestrictionPlanNudgeTimer) {
					clearTimeout(this.domainRestrictionPlanNudgeTimer);
					this.domainRestrictionPlanNudgeTimer = null;
				}
			}
		},
	},
	components: {
		DoubleConfirmDangerDialog,
		SessionsHistoryDialog,
		PaymentsHistoryDialog,
		SettingsWindowItem,
		LoginSettingsDialog,
	},
});
